import { api, errMsg } from "@/lib/api";
import React, { useState, useEffect } from "react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { sendFirebaseOtp, verifyFirebaseOtp } from "@/lib/firebase";
import {
  X,
  Lock,
  Phone,
  User,
  Mail,
  LogIn,
  UserPlus,
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";

export default function CustomerAuthModal({
  isOpen,
  onClose,
  onSuccess,
  initialTab = "login",
}) {
  const { login, firebaseLogin, register, resetPassword } = useCustomerAuth();
  const [tab, setTab] = useState(initialTab); // "login" | "otp_login" | "register" | "forgot"
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  // Form states
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [nextOtpAt, setNextOtpAt] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otpSent, setOtpSent] = useState(false);

  // Update cooldown countdown timer
  useEffect(() => {
    if (nextOtpAt <= Date.now()) {
      setCooldownRemaining(0);
      return;
    }
    const timer = setInterval(() => {
      const rem = Math.max(0, Math.ceil((nextOtpAt - Date.now()) / 1000));
      setCooldownRemaining(rem);
      if (rem <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [nextOtpAt]);

  // Reset tab on modal open
  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
      setError("");
      setOtp("");
      setOtpSent(false);
      setConfirmationResult(null);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  // Clean 10-digit phone
  const cleanPhone = (phone || "").replace(/\D/g, "").slice(-10);

  // Send OTP handler (Firebase Primary, Server fallback)
  const handleSendOtp = async () => {
    if (!cleanPhone || cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    if (cooldownRemaining > 0) {
      setError(`Please wait ${cooldownRemaining}s before requesting another code`);
      return;
    }

    setSendingOtp(true);
    setError("");

    try {
      // 1. Primary: Firebase Phone Authentication
      const result = await sendFirebaseOtp(cleanPhone, "firebase-recaptcha-container");
      setConfirmationResult(result);
      setOtpSent(true);
      setNextOtpAt(Date.now() + 45000);
      toast.success(`Verification code sent to +91 ${cleanPhone} via SMS! 📱`);
    } catch (fbErr) {
      console.warn("[Firebase] Phone auth error, attempting server fallback:", fbErr);
      try {
        // 2. Fallback: Server SMS / gateway
        await api.post("/customer/auth/send-otp", { phone: cleanPhone });
        setConfirmationResult(null);
        setOtpSent(true);
        setNextOtpAt(Date.now() + 45000);
        toast.success(`Verification code requested for +91 ${cleanPhone}. Check your SMS! 📱`);
      } catch (srvErr) {
        const msg = fbErr?.message || errMsg(srvErr);
        setError(msg || "Could not send verification code. Please check your phone number.");
      }
    } finally {
      setSendingOtp(false);
    }
  };

  // Helper to verify OTP (Firebase confirm or return code)
  const resolveVerifiedTokenOrCode = async () => {
    const cleanOtp = (otp || "").replace(/\D/g, "").slice(0, 6);
    if (!cleanOtp || cleanOtp.length !== 6) {
      throw new Error("Please enter the complete 6-digit verification code");
    }

    if (confirmationResult && typeof confirmationResult.confirm === "function") {
      const { idToken } = await verifyFirebaseOtp(confirmationResult, cleanOtp);
      return { firebaseToken: idToken, otp: cleanOtp };
    }
    return { firebaseToken: null, otp: cleanOtp };
  };

  // 1. Password Login
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone.trim()) {
      setError("Please enter your mobile number or email");
      return;
    }
    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    const res = await login(phone.trim(), password);
    setLoading(false);

    if (res.ok) {
      toast.success(`Welcome back, ${res.customer?.name || "Customer"}! 🎉`);
      if (onSuccess) onSuccess(res.customer);
      if (onClose) onClose();
    } else {
      setError(res.error || "Invalid mobile number or password");
    }
  };

  // 2. Direct OTP Login (Firebase / SMS)
  const handleOtpLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!cleanPhone || cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!otpSent) {
      setError("Please tap 'Send Code' to receive your verification code");
      return;
    }

    setLoading(true);
    try {
      const { firebaseToken, otp: code } = await resolveVerifiedTokenOrCode();
      let res;
      if (firebaseToken) {
        res = await firebaseLogin({ idToken: firebaseToken, name: name.trim() || undefined });
      } else {
        const { data } = await api.post("/customer/auth/verify-otp", {
          phone: cleanPhone,
          otp: code,
          name: name.trim() || undefined,
        });
        if (data.access_token) {
          localStorage.setItem("gh_customer_token", data.access_token);
        }
        res = { ok: true, customer: data.customer };
      }

      setLoading(false);
      if (res.ok) {
        toast.success(`Signed in successfully! Welcome, ${res.customer?.name || "Customer"}! 👑`);
        if (onSuccess) onSuccess(res.customer);
        if (onClose) onClose();
      } else {
        setError(res.error || "Verification failed. Please try again.");
      }
    } catch (err) {
      setLoading(false);
      setError(err?.message || "Invalid or expired verification code");
    }
  };

  // 3. Register Account
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Please enter your full name");
      return;
    }
    if (!cleanPhone || cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (!otpSent) {
      setError("Please tap 'Send code' to verify your phone number");
      return;
    }

    setLoading(true);
    try {
      const { firebaseToken, otp: code } = await resolveVerifiedTokenOrCode();
      const res = await register({
        name: name.trim(),
        phone: cleanPhone,
        password,
        email: email.trim(),
        otp: code,
        firebaseToken,
      });
      setLoading(false);

      if (res.ok) {
        toast.success(`Account ready! Welcome to Velpula Mess, ${res.customer?.name}! ✨`);
        if (onSuccess) onSuccess(res.customer);
        if (onClose) onClose();
      } else {
        setError(res.error || "Registration failed. Please check your details.");
      }
    } catch (err) {
      setLoading(false);
      setError(err?.message || "Invalid verification code. Please check your SMS.");
    }
  };

  // 4. Reset Password
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!cleanPhone || cleanPhone.length !== 10) {
      setError("Please enter your valid 10-digit mobile number");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (!otpSent) {
      setError("Please tap 'Send code' to verify your phone number");
      return;
    }

    setLoading(true);
    try {
      const { firebaseToken, otp: code } = await resolveVerifiedTokenOrCode();
      const res = await resetPassword({
        phone: cleanPhone,
        newPassword: password,
        name: name.trim() || undefined,
        otp: code,
        firebaseToken,
      });
      setLoading(false);

      if (res.ok) {
        toast.success(`Password updated & signed in! Welcome, ${res.customer?.name || "Customer"}! ✨`);
        if (onSuccess) onSuccess(res.customer);
        if (onClose) onClose();
      } else {
        setError(res.error || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      setLoading(false);
      setError(err?.message || "Invalid verification code. Please check your SMS.");
    }
  };

  // Shared OTP Input Component
  const otpSection = (
    <div className="space-y-1.5 p-3 rounded-2xl bg-stone-950/90 border border-amber-900/30">
      <div className="flex items-center justify-between">
        <label htmlFor="customer-otp" className="block text-xs font-semibold text-stone-300">
          6-Digit SMS Verification Code <span className="text-amber-500">*</span>
        </label>
        <span className="text-[10px] text-stone-400">Firebase Secured</span>
      </div>
      <div className="flex gap-2">
        <input
          id="customer-otp"
          aria-label="SMS verification code"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="e.g. 123456"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full min-w-0 rounded-xl px-3 py-2.5 bg-stone-900 border border-stone-800 text-stone-100 placeholder-stone-600 text-base tracking-widest font-mono text-center focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
        />
        <button
          type="button"
          disabled={sendingOtp || cooldownRemaining > 0}
          onClick={handleSendOtp}
          className="rounded-xl px-3.5 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold text-xs whitespace-nowrap shadow-md transition"
        >
          {sendingOtp
            ? "Sending…"
            : cooldownRemaining > 0
            ? `Resend (${cooldownRemaining}s)`
            : otpSent
            ? "Resend Code"
            : "Send Code"}
        </button>
      </div>
      <p className="text-[11px] text-stone-400">
        {otpSent
          ? "Code sent to your phone. Valid for 5 minutes."
          : "Tap 'Send Code' to receive a 6-digit verification code on your mobile."}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-in fade-in">
      <div className="relative w-full max-w-md max-h-[92vh] overflow-y-auto bg-stone-900 border border-amber-900/40 rounded-3xl p-6 md:p-7 shadow-2xl text-stone-100">
        {/* Invisible reCAPTCHA container for Firebase Phone Auth */}
        <div id="firebase-recaptcha-container"></div>

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-stone-400 hover:text-white hover:bg-stone-800 transition"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header Branding */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-stone-950 border border-amber-600/30 p-1 shadow-lg mb-2">
            <img src="/logo.png" alt="Logo" className="w-full h-full object-contain rounded-full" />
          </div>
          <h2 className="text-2xl font-bold font-serif text-amber-100">Velpula Mess</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            {tab === "login"
              ? "Sign in with password or 1-click mobile OTP"
              : tab === "otp_login"
              ? "Fast, passwordless mobile number sign-in"
              : tab === "register"
              ? "Create your account for loyalty rewards & saved addresses"
              : "Verify your phone to set a new password"}
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="grid grid-cols-4 bg-stone-950/80 p-1 rounded-2xl border border-stone-800 mb-5 text-[11px]">
          <button
            type="button"
            onClick={() => {
              setTab("login");
              setError("");
            }}
            className={`flex items-center justify-center gap-1 py-2 font-semibold rounded-xl transition ${
              tab === "login"
                ? "bg-amber-600 text-white shadow-md"
                : "text-stone-400 hover:text-stone-200"
            }`}
          >
            <LogIn className="w-3 h-3" />
            Password
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("otp_login");
              setError("");
            }}
            className={`flex items-center justify-center gap-1 py-2 font-semibold rounded-xl transition ${
              tab === "otp_login"
                ? "bg-amber-600 text-white shadow-md"
                : "text-stone-400 hover:text-stone-200"
            }`}
          >
            <Smartphone className="w-3 h-3" />
            OTP Login
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("register");
              setError("");
            }}
            className={`flex items-center justify-center gap-1 py-2 font-semibold rounded-xl transition ${
              tab === "register"
                ? "bg-amber-600 text-white shadow-md"
                : "text-stone-400 hover:text-stone-200"
            }`}
          >
            <UserPlus className="w-3 h-3" />
            Register
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("forgot");
              setError("");
            }}
            className={`flex items-center justify-center gap-1 py-2 font-semibold rounded-xl transition ${
              tab === "forgot"
                ? "bg-amber-600 text-white shadow-md"
                : "text-stone-400 hover:text-stone-200"
            }`}
          >
            <KeyRound className="w-3 h-3" />
            Reset
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-950/60 border border-red-800/60 text-red-200 text-xs flex flex-col gap-1.5 animate-shake">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
              <span>{error}</span>
            </div>
            {tab === "login" && (
              <button
                type="button"
                onClick={() => {
                  setTab("otp_login");
                  setError("");
                }}
                className="text-left text-amber-400 hover:underline font-semibold text-[11px] pl-6"
              >
                Sign in using mobile OTP instead →
              </button>
            )}
          </div>
        )}

        {/* TAB 1: Password Sign In */}
        {tab === "login" && (
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1.5">
                Mobile Number or Email
              </label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="text"
                  required
                  placeholder="10-digit mobile or email address"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-xs font-semibold text-stone-300">Password</label>
                <button
                  type="button"
                  onClick={() => {
                    setTab("forgot");
                    setError("");
                  }}
                  className="text-xs text-amber-400 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-3 text-stone-500 hover:text-stone-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 mt-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 text-white font-bold rounded-xl shadow-lg hover:shadow-amber-500/25 transition duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign In
                </>
              )}
            </button>

            <div className="relative my-3 text-center">
              <span className="text-[11px] text-stone-500 bg-stone-900 px-2 relative z-10">or</span>
              <div className="absolute inset-0 top-1/2 border-t border-stone-800 -z-0"></div>
            </div>

            <button
              type="button"
              onClick={() => {
                setTab("otp_login");
                setError("");
              }}
              className="w-full py-2.5 bg-stone-950 hover:bg-stone-800 border border-stone-700 text-stone-200 font-semibold text-xs rounded-xl transition flex items-center justify-center gap-2"
            >
              <Smartphone className="w-4 h-4 text-amber-500" />
              Sign in with 1-Click Mobile OTP
            </button>

            <p className="text-center text-xs text-stone-400 mt-2">
              Don't have an account yet?{" "}
              <button
                type="button"
                onClick={() => {
                  setTab("register");
                  setError("");
                }}
                className="text-amber-400 hover:underline font-semibold"
              >
                Create one in 10 seconds
              </button>
            </p>
          </form>
        )}

        {/* TAB 2: Direct OTP Login */}
        {tab === "otp_login" && (
          <form onSubmit={handleOtpLoginSubmit} className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Mobile Number <span className="text-amber-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="tel"
                  required
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setOtpSent(false);
                    setConfirmationResult(null);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Your Name <span className="text-stone-500 text-[10px]">(Optional)</span>
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="text"
                  placeholder="e.g. Rahul Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            {otpSection}

            <button
              type="submit"
              disabled={loading || !otpSent || otp.length !== 6}
              className="w-full py-3 mt-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 text-white font-bold rounded-xl shadow-lg hover:shadow-amber-500/25 transition duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  Verify & Sign In
                </>
              )}
            </button>

            <p className="text-center text-xs text-stone-400 mt-2">
              Prefer password?{" "}
              <button
                type="button"
                onClick={() => {
                  setTab("login");
                  setError("");
                }}
                className="text-amber-400 hover:underline font-semibold"
              >
                Sign in with password
              </button>
            </p>
          </form>
        )}

        {/* TAB 3: Register Form */}
        {tab === "register" && (
          <form onSubmit={handleRegisterSubmit} className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Your Full Name <span className="text-amber-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Mobile Number <span className="text-amber-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="tel"
                  required
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setOtpSent(false);
                    setConfirmationResult(null);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Email Address <span className="text-stone-500 text-[10px]">(Optional)</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="email"
                  placeholder="rahul@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Create Password <span className="text-amber-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-3 text-stone-500 hover:text-stone-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {otpSection}

            <button
              type="submit"
              disabled={loading || !otpSent || otp.length !== 6}
              className="w-full py-3 mt-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 text-white font-bold rounded-xl shadow-lg hover:shadow-amber-500/25 transition duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Create Customer Account
                </>
              )}
            </button>

            <p className="text-center text-xs text-stone-400 mt-2">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setTab("login");
                  setError("");
                }}
                className="text-amber-400 hover:underline font-semibold"
              >
                Sign in here
              </button>
            </p>
          </form>
        )}

        {/* TAB 4: Reset Password Form */}
        {tab === "forgot" && (
          <form onSubmit={handleResetSubmit} className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                Your Mobile Number <span className="text-amber-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type="tel"
                  required
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setOtpSent(false);
                    setConfirmationResult(null);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-300 mb-1">
                New Password <span className="text-amber-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 w-4 h-4 text-stone-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Set your new password (min 8 chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-stone-950 border border-stone-800 rounded-xl text-stone-100 placeholder-stone-600 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-3 text-stone-500 hover:text-stone-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {otpSection}

            <button
              type="submit"
              disabled={loading || !otpSent || otp.length !== 6}
              className="w-full py-3 mt-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 text-white font-bold rounded-xl shadow-lg hover:shadow-amber-500/25 transition duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <KeyRound className="w-4 h-4" />
                  Reset Password & Sign In
                </>
              )}
            </button>

            <p className="text-center text-xs text-stone-400 mt-2">
              Remember your password?{" "}
              <button
                type="button"
                onClick={() => {
                  setTab("login");
                  setError("");
                }}
                className="text-amber-400 hover:underline font-semibold"
              >
                Sign in directly
              </button>
            </p>
          </form>
        )}

        <div className="pt-3 mt-4 border-t border-stone-800 text-center">
          <a
            href="/login"
            className="text-[11px] text-stone-400 hover:text-amber-400 font-medium inline-flex items-center gap-1 transition"
          >
            Restaurant Staff / Cashier? <span className="text-amber-400 font-bold underline">Staff & POS Login →</span>
          </a>
        </div>
      </div>
    </div>
  );
}
